import { type Poly, ensurePositive, pointInPolygon } from '../geom/polygon';
import { type Vec2, add, dist, norm, scale, sub } from '../geom/vec';
import type { MeshBuilder } from '../mesh/builder';
import { type RGB, addFlat } from '../mesh/extrude';
import { hash01 } from '../rng';

/** Fake shop names for the sign atlas (src/render/buildingDetails.ts draws them). */
export const SHOP_NAMES = [
  'DELI', 'PAWN SHOP', 'LAUNDROMAT', 'PIZZA', 'BAR', 'HARDWARE', 'BOOKS', 'MARKET',
  'TAILOR', 'CAFE', 'LIQUOR', 'FLOWERS', 'BAKERY', 'DINER', 'PHARMACY', 'BARBER',
  'NOODLES', 'SHOES', 'RECORDS', 'TATTOO', 'DRY CLEAN', 'GROCERY', 'DONUTS', 'LOANS',
  'PETS', 'VIDEO', 'GYM', 'CHECKS CASHED', 'TOYS', 'OPTICIAN', 'BAGELS', 'HOTEL',
];

/** A world-space vertical quad a→b between z0 and z1, facing right of a→b (outward for CCW rings). */
function quad(mb: MeshBuilder, a: Vec2, b: Vec2, z0: number, z1: number, c: RGB): void {
  mb.quad(a.x, a.y, z0, b.x, b.y, z0, b.x, b.y, z1, a.x, a.y, z1, c);
}

/**
 * A projecting band (cornice, string course) along the edges of `poly` (counter-clockwise) where
 * `banded[e]`, `out` cm proud of the wall from z0 to z1: front, top, underside and ends. Each band
 * runs on over a convex corner to cover it (the next band starts at the corner), and stops short of a
 * concave one, so neighbouring bands never overlap (z-fighting).
 */
export function addBands(mb: MeshBuilder, ring: Poly, banded: boolean[], z0: number, z1: number, out: number, color: RGB): void {
  const poly = ensurePositive(ring);
  const n = poly.length;
  for (let e = 0; e < n; e++) {
    if (!banded[e]) continue;
    const a = poly[e];
    const b = poly[(e + 1) % n];
    if (dist(a, b) < 2 * out + 10) continue;
    const dir = norm(sub(b, a));
    const nrm = { x: dir.y, y: -dir.x };
    // End at b: over a convex corner (to the next band), short of a concave one.
    const c = poly[(e + 2) % n];
    const next = norm(sub(c, b));
    const turn = dir.x * next.y - dir.y * next.x;
    const nextBanded = banded[(e + 1) % n];
    const endExt = turn > 0.2 && nextBanded ? out : turn < -0.2 ? -out : 0;
    // Start at a: short of a concave corner with the previous edge.
    const p = poly[(e - 1 + n) % n];
    const prev = norm(sub(a, p));
    const turnA = prev.x * dir.y - prev.y * dir.x;
    const startCut = turnA < -0.2 ? out : 0;
    const A = add(a, scale(dir, startCut));
    const B = add(b, scale(dir, endExt));
    const Ao = add(A, scale(nrm, out));
    const Bo = add(B, scale(nrm, out));
    quad(mb, Ao, Bo, z0, z1, color);
    addFlat(mb, ensurePositive([A, B, Bo, Ao]), [], z1, color);
    addFlat(mb, ensurePositive([A, B, Bo, Ao]), [], z0, color, true);
    quad(mb, A, Ao, z0, z1, color);
    quad(mb, Bo, B, z0, z1, color);
  }
}

export interface RoofItem {
  /** Corner in generation space, heading (deg), size (cm along, cm up, m deep), colour. */
  x: number;
  y: number;
  yaw: number;
  w: number;
  h: number;
  d: number;
  color: RGB;
}

const GREY: RGB = [0.62, 0.63, 0.64];
const DARK: RGB = [0.32, 0.33, 0.35];
const BRICK: RGB = [0.5, 0.3, 0.24];

/**
 * Roof clutter inside `roof` (a flat roof, counter-clockwise), aligned with its first edge: AC units,
 * vent stacks and, on towers, mechanical rooms and an antenna. Each is kept 60 cm inside the roof
 * and off `avoid` (e.g. the shaft housing); deterministic in (seed, key).
 */
export function roofItems(seed: number, key: number, roof: Poly, tower: boolean, avoid: Poly[] = []): RoofItem[] {
  const poly = ensurePositive(roof);
  const a = poly[0];
  const dir = norm(sub(poly[1], a));
  const nrm = { x: -dir.y, y: dir.x }; // into the roof for a CCW ring
  const yaw = (Math.atan2(dir.y, dir.x) * 180) / Math.PI;
  const us = poly.map((q) => (q.x - a.x) * dir.x + (q.y - a.y) * dir.y);
  const vs = poly.map((q) => (q.x - a.x) * nrm.x + (q.y - a.y) * nrm.y);
  const [u0, u1, v0, v1] = [Math.min(...us), Math.max(...us), Math.min(...vs), Math.max(...vs)];
  const specs: { w: number; h: number; d: number; color: RGB }[] = [];
  if (tower) {
    const n = 2 + Math.floor(hash01(seed, key, 1) * 4);
    for (let i = 0; i < n; i++) specs.push({ w: 250 + hash01(seed, key, 10 + i) * 350, h: 180 + hash01(seed, key, 20 + i) * 220, d: 2 + hash01(seed, key, 30 + i) * 3, color: i % 2 ? GREY : DARK });
    if (hash01(seed, key, 2) < 0.6) specs.push({ w: 25, h: 600 + hash01(seed, key, 3) * 900, d: 0.25, color: DARK });
  } else {
    if (hash01(seed, key, 4) < 0.65) specs.push({ w: 90, h: 70, d: 0.7, color: GREY });
    if (hash01(seed, key, 5) < 0.5) specs.push({ w: 25, h: 90, d: 0.25, color: DARK });
    if (hash01(seed, key, 6) < 0.5) specs.push({ w: 60, h: 130, d: 0.45, color: BRICK });
  }
  const out: RoofItem[] = [];
  const placed: Poly[] = [];
  specs.forEach((s, i) => {
    for (let tries = 0; tries < 8; tries++) {
      const u = u0 + 60 + hash01(seed, key, 100 + i, tries) * Math.max(0, u1 - u0 - 120 - s.w);
      const v = v0 + 60 + hash01(seed, key, 200 + i, tries) * Math.max(0, v1 - v0 - 120 - s.d * 100);
      const o = add(add(a, scale(dir, u)), scale(nrm, v));
      const rect: Poly = [o, add(o, scale(dir, s.w)), add(add(o, scale(dir, s.w)), scale(nrm, s.d * 100)), add(o, scale(nrm, s.d * 100))];
      const inside = rect.every((q) => pointInPolygon(q, poly));
      const clear = [...avoid, ...placed].every((p) => !rect.some((q) => pointInPolygon(q, p)) && !p.some((q) => pointInPolygon(q, rect)));
      if (!inside || !clear) continue;
      placed.push(rect);
      // The instance's frame: x along the edge, z outward; "outward" here is into the roof (nrm), so the
      // box is placed from its far corner with the heading turned around.
      const far = add(o, scale(dir, s.w));
      out.push({ x: far.x, y: far.y, yaw: yaw + 180, w: s.w, h: s.h, d: s.d, color: s.color });
      break;
    }
  });
  return out;
}
